import axios from 'axios';
import safeStorage from './utility/safeStorage';

const API_URL = import.meta.env.VITE_API_BASE_URL;

// ─── Axios Instance ─────────
const axiosInstance = axios.create({
    baseURL: API_URL,
});

// ─── Request Interceptor ──────
// Automatically attaches the latest accessToken to every request
axiosInstance.interceptors.request.use(
    async (config) => {
        let token = safeStorage.getItem('accessToken');

        if (!token) {
            await new Promise(resolve => setTimeout(resolve, 100));
            token = safeStorage.getItem('accessToken');
        }

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ─── Refresh Token Logic ──────
let isRefreshing = false;       // prevents multiple simultaneous refresh calls
let failedQueue = [];           // queues requests that arrived during a refresh

// Once refresh succeeds/fails, resolve or reject all queued requests
const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// ─── Response Interceptor ───────
// Intercepts 401 errors, refreshes the token, and retries the original request
axiosInstance.interceptors.response.use(
    (response) => response, // Pass through successful responses
    async (error) => {
        const originalRequest = error.config;
        // Only attempt refresh on 401 and if we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
            // If a refresh is already in progress, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((newToken) => {
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return axiosInstance(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            // Mark this request so it won't loop if the retry also 401s
            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = safeStorage.getItem('refreshToken');

            if (!refreshToken) {
                // No refresh token available — force logout
                handleLogout(error.config?.navigate);
                return Promise.reject(error);
            }

            try {
                // Call your refresh token endpoint
                const { data } = await axios.post(`${API_URL}auth/RefreshToken`, {
                    refreshToken: refreshToken
                }
                );
                // const { data } = await axios.post(`${API_URL}/auth/RefreshToken`, {
                //     refreshToken,
                // });

                const newAccessToken = data.accessToken;
                const newRefreshToken = data.refreshToken; // if your API rotates refresh tokens

                // Persist the new tokens
                safeStorage.setItem('accessToken', newAccessToken);
                if (newRefreshToken) {
                    safeStorage.setItem('refreshToken', newRefreshToken);
                }

                // Update the Authorization header on the instance and original request
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

                processQueue(null, newAccessToken); // resolve all queued requests
                return axiosInstance(originalRequest); // retry the original request
            } catch (refreshError) {
                processQueue(refreshError, null); // reject all queued requests
                handleLogout(error.config?.navigate); // refresh failed — log out
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// ─── Logout Helper ─────────
const handleLogout = (navigate) => {
    safeStorage.removeItem('accessToken');
    safeStorage.removeItem('refreshToken');
    if (navigate) {
        navigate('/login');
    } else {
        window.location.href = '/login'; // fallback if navigate isn't available
    }
};

// ─── API Methods ───────

export const getData = async (endpoint, params = {}, navigate = null) => {
    const requestId = Date.now();

    try {
        const response = await axiosInstance.get(endpoint, {
            params,
            navigate, // passed via config so interceptor can use it
        });

        return response.data;
    } catch (error) {
        if (error.code === "ERR_CANCELED") {
            //console.warn(`🔴 GET CANCELED [${requestId}] → ${endpoint}`);
            return null; // ❗ DO NOT THROW
        }
        const enhancedError = {
            message: error.message,
            response: {
                status: error.response?.status,
                data: error.response?.data,
            },
        };
        //console.error(`❌ GET ERROR [${requestId}] → ${endpoint}`, error);

        handleError(error, navigate);
        throw enhancedError;
    }
};

export const getDataAuth = async (endpoint, params = {}, navigate = null) => {

    const token = safeStorage.getItem('accessToken');
    try {
        const response = await axiosInstance.get(API_URL + endpoint, {
            headers: {
                Authorization: `Bearer ${token}`, // Include the token in the headers
            },
        });

        return response.data;
    } catch (error) {
        handleError(error, navigate);
        throw error; // Re-throw error for specific handling if needed
    }
};

export const postData = async (endpoint, data, navigate = null, isFormData = false) => {
    //const requestId = Date.now();
    try {
        const headers = {};
        if (!isFormData) {
            headers['Content-Type'] = 'application/json';
        }

        const response = await axiosInstance.post(endpoint, data, { headers, navigate });
        return response.data;
    } catch (error) {
        
        if (error.code === "ERR_CANCELED") {
            return null; // ❗ DO NOT THROW
        }
        handleError(error, navigate);

        if (error.response?.data) {
            return error.response.data;
        }
        return { ok: false, errors: { message: 'Network error' } };
    }
};

export const postData1 = async (endpoint, data, navigate = null) => {
    try {
        const response = await axiosInstance.post(endpoint, JSON.stringify(data), {
            headers: { 'Content-Type': 'application/json' },
            navigate,
        });
        return response.data;
    } catch (error) {
        handleError(error, navigate);
        throw error;
    }
};

export const putData = async (endpoint, data, navigate = null) => {
    try {
        const response = await axiosInstance.put(endpoint, data, {
            headers: { 'Content-Type': 'application/json' },
            navigate,
        });
        return response.data;
    } catch (error) {
        handleError(error, navigate);
        throw error;
    }
};

export const deleteData = async (endpoint, navigate = null) => {
    try {
        const response = await axiosInstance.delete(endpoint, { navigate });
        return response.data;
    } catch (error) {
        handleError(error, navigate);
        throw error;
    }
};

// ─── Error Handler ───────
const handleError = (error, navigate) => {
    if (error.response?.status === 403) {
        console.error('Forbidden: You do not have permission.');
    } else if (error.response?.status >= 500) {
        console.error('Server error. Please try again later.');
    }
    // 401 is handled by the interceptor above, no need to handle here
};

export const logoutApi = () =>
    axios.post(
        `${API_URL}auth/Logout`,
        {},
        { withCredentials: true }
    );

export const LogoutAll = () =>
    axios.post(
        `${API_URL}auth/LogoutAll`,
        {},
        { withCredentials: true }
    );